import styled, {css} from "styled-components";
import {SiteLink as RouterLink} from "nav/SiteLink";

/**
 * Externalized styles for {@link ./SiteFooter} (kitecorp/transferify-ui#2394).
 *
 * One hairline row at the foot of the app scroller. It is deliberately the smallest thing that can
 * carry four links: § 312k Abs. 2 S. 4 BGB asks for a control that is "leicht zugänglich", and
 * LG München I 3 HK O 13796/24 held that a cancellation control buried among MANY footer links is
 * not — so the point of this footer is the short row, not the footer.
 */

/**
 * The custom property {@link ./SiteFooter} publishes this row's measured height on, for the boxes that
 * budget against the window while being laid out above it (ui#2631).
 *
 * Named here rather than typed at each end: the publisher and every reader have to agree on the
 * spelling, and a reader whose name had drifted would silently fall back to its own `0px` and reserve
 * nothing — the defect it exists to prevent, wearing a passing test.
 */
export const SITE_FOOTER_RESERVE_PROPERTY = "--site-footer-reserve";

/**
 * The room a control fixed in a corner of the window asks this row to keep below its links, published on the document
 * root for as long as the control is on the page (kitecorp/transferify-ui#4043).
 *
 * This row is the last thing in the app scroller, so at the very end of a page nothing can lift it any further: a link
 * under a fixed control would stay there. Growing the row's own bottom padding gives the scroller that room, in the
 * row's own colour, and moves nothing above it.
 */
export const SITE_FOOTER_CLEARANCE_PROPERTY = "--site-footer-clearance";

/**
 * Sizes a box to the SURFACE this row leaves rather than to the window (kitecorp/transferify-ui#2635).
 *
 * <p>The row is in the app scroller's flow, so on every route that carries it the surface above ends one
 * row short of the window. Four boxes had to know that, and each of them wrote its own version: the
 * create card's cap subtracted the reserve by hand while still naming `100vh`, the reminder card's stage
 * and the router error surface named the window alone, and the landing hero named it in a rule that
 * matched nothing. Four copies of one arithmetic is four places for the next reader to find three of.
 *
 * <p>It is ONE declaration with the pre-2022 `vh` fallback beneath it, for the reason `AppComponents`'
 * `Main` records (ui#687): `100vh` is the LARGE viewport, so a box pinned to it is taller than what a
 * visitor with the address bar showing can see, and `dvh` tracks the bar. Browsers without `dvh` take
 * only the first line and keep today's behaviour. Because the pair lives here, `100vh` appears once for
 * every caller instead of once per caller.
 *
 * <p>The reserve is the row's MEASURED height and the row REMOVES the property when it unmounts, so on a
 * surface with no row — the landing, the private destinations, the delivery pages — every caller's own
 * `0px` fallback applies and this is exactly `100dvh`. That is why no caller needs a route rule of its
 * own, and why a caller may take this declaration before knowing whether its surface carries a row.
 *
 * @param property the box dimension to declare.
 * @param alsoSubtract further terms this caller takes off the surface, each a bare CSS length or `var()`.
 */
export function surfaceHeight(
	property: "height" | "min-height" | "max-height",
	...alsoSubtract: string[]
) {
	const taken = [`var(${SITE_FOOTER_RESERVE_PROPERTY}, 0px)`, ...alsoSubtract]
		.map(term => ` - ${term}`)
		.join("");
	return css`
		${property}: calc(100vh${taken});
		${property}: calc(100dvh${taken});
	`;
}

/**
 * `flex: none` plus `margin-top: auto`, and both are load-bearing in different states of the shell.
 *
 * `flex: none` keeps the row at its own height while `Content`'s page slot takes the rest, so a
 * single-screen surface (the landing, the recipient page) simply gets that much less room instead of
 * pushing the page into scroll. `margin-top: auto` is for the other case — a content page shorter
 * than the window, where the row would otherwise sit directly under the last paragraph with empty
 * ground below it. Auto margins are distributed AFTER flex growth, so on a page whose slot already
 * grows this adds nothing.
 *
 * No `position: fixed`: a fixed row would cover the page it sits on, and `consent/scrollReach` skips
 * fixed controls when it measures what the cookie bar can trap — a fixed footer would be stranded
 * under that bar forever with no room minted for it.
 */
export const Root = styled.footer`
	flex: none;
	margin-top: auto;
	box-sizing: border-box;
	width: 100%;
	padding: 10px 24px max(10px, var(${SITE_FOOTER_CLEARANCE_PROPERTY}, 0px));
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	background-color: ${({theme}) => theme.tokens.paper};
	text-align: center;

	@media (max-width: 640px) {
		padding-inline: 16px;
	}
`;

/**
 * One row entry, and `display: flex` on it is what makes the row as tall as it says it is
 * (kitecorp/transferify-ui#2634).
 *
 * A list item is a block container, and {@link FooterLink} is inline-level, so the item used to form a
 * LINE BOX — and the inherited strut then added its own descender below the 44px control. The app's line
 * height is fluid, so the excess grew with the viewport rather than being a constant: the row measured
 * 65px at 375x812 but 66.8px at 768x900 and 69.8px at 1280x900 and 1440x900, against a design of
 * 10 + 44 + 10 + 1. Every storefront surface gave up that difference, and {@link ./SiteFooter} publishes
 * what it MEASURES, so nothing anywhere reported it.
 *
 * As a flex container the item forms no line box and no strut applies, so it is exactly the control's
 * 44px. `e2e/public-navigation.spec.ts` reads back the item's and the control's heights and the row count.
 * The item also keeps its `listitem` role: `display` on a list item is where list semantics get dropped by
 * accident, and the same spec counts the row's list and its four items so a fix that cost the
 * accessibility tree could not pass as one.
 */
export const LinkRow = styled.li`
	display: flex;
`;

/** The row itself: a plain list, wrapping rather than scrolling sideways on a narrow phone. */
export const Links = styled.ul`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	align-items: center;
	gap: 6px 18px;
	margin: 0;
	padding: 0;
	list-style: none;
`;

/**
 * 13px is small, and that is the ceiling rather than the floor: the row is chrome, not content. The
 * ink is `ink2`, which clears 4.5:1 against `paper` in both modes — measured 6.5:1 light
 * (#585e6b on #ffffff) and 8.3:1 dark (#a4a9b4 on #0b0c10) — and hover/focus lift it to full `ink`
 * instead of changing hue.
 *
 * The 44px touch height is the standing rule for every control in this app, and it is bought with
 * `min-height` + centring rather than padding so the printed row stays 13px tall to the eye.
 */
export const FooterLink = styled(RouterLink)`
	display: inline-flex;
	align-items: center;
	min-height: 44px;
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 13px;
	line-height: 1.2;
	text-decoration: none;

	&:hover,
	&:focus-visible {
		color: ${({theme}) => theme.tokens.ink};
		text-decoration: underline;
	}
`;
